import {Injectable, OnDestroy, OnInit} from '@angular/core';
import {Subject, takeUntil, timer} from 'rxjs';
import {CoreService} from "../../common/core.service";
import {Observable} from "rxjs/internal/Observable";
import {map, shareReplay} from "rxjs/operators";
import {AuthService} from "../../auth/auth-service";
import IUserInactivityViewData = NCVIB.app.login.UserInactivity.IUserInactivityViewData;

@Injectable({
    providedIn: 'root'
})
export class UserInactivityService implements OnDestroy {
    private activityEvents = ['click', 'mousemove', 'keydown'];
    private destroy$ = new Subject<void>();
    private maxInactivityBeforeLogout: number = 1000;
    private intervalId: ReturnType<typeof setInterval> | undefined;
    public isTrackingInactivity: boolean = false;
    timeLeft$!: Observable<number>;

    constructor(
        private core: CoreService,
        private authService: AuthService) {
        this.timeLeft$ = timer(0, 1000).pipe(
            map(() => Math.round(this.getTimeLeft() / 1000)),
            takeUntil(this.destroy$),
            shareReplay(1)
        );
    }

    public start() {
        this.core.get<IUserInactivityViewData>(
            `api/v2/user-inactivity/get-view-data`,
            "Get User Inactivity View Data")
            .subscribe(viewData => {
                if (viewData.maxInactivityBeforeLogout) {
                    // console.log(`Inactivity will be tracked: ${viewData.maxInactivityBeforeLogout}s timeout`)
                    this.maxInactivityBeforeLogout = viewData.maxInactivityBeforeLogout;
                    this.listenForUserActivity();
                    this.intervalId = setInterval(() => this.verify(), (this.maxInactivityBeforeLogout * 1000));
                    this.isTrackingInactivity = true;

                } else {
                    // console.log("Inactivity will not be tracked!!");
                }
            });
    }

    private listenForUserActivity() {
        this.activityEvents.forEach(event => window.addEventListener(event, () => {
            const now = new Date().getTime();
            document.cookie = "lastActivity=" + now + ";path=/";
        }));
    }

    public getTimeLeft(): number {
        const lastActivity = parseInt(this.getCookie('lastActivity'), 10);
        const now = new Date().getTime();
        return this.maxInactivityBeforeLogout * 1000 - (now - lastActivity);
    }

    private verify() {
        const timeLeft = this.getTimeLeft();
        console.log(`Verify Inactivity Logout: timeLeft=${timeLeft}`);
        if (timeLeft < 0) {
            console.log(`Session has expired due to inactivity`);
            this.logoffUser();
        } else {
            clearInterval(this.intervalId);
            this.intervalId = setInterval(() => this.verify(), timeLeft + 100);
        }
    }

    private logoffUser() {
        console.log('User has been inactive for', this.maxInactivityBeforeLogout, 'ms');
        // window.location.href = '/accounts/logoff?message=LoggedOutDueToInactivity';
        this.authService.signOut();
    }

    private getCookie(name: string): string {
        let cookieValue = null;
        if (document.cookie && document.cookie !== '') {
            const cookies = document.cookie.split(';');
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i].trim();
                // Does this cookie string begin with the name we want?
                if (cookie.substring(0, name.length + 1) === (name + '=')) {
                    cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                    break;
                }
            }
        }
        return cookieValue || "";
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.activityEvents.forEach(event =>
            window.removeEventListener(event, () => {
            })
        );
        clearInterval(this.intervalId);
    }
}
